import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CircleCheck, Copy, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { copyToClipboard } from '../../lib/clipboard';
import { publicBaseUrl, scopeSentence, testKey } from '../../lib/developerApi';

/**
 * Show a freshly minted (or rolled) key — the only time it is ever visible.
 *
 * The raw key lives in this component's props and nowhere else: it is not put in
 * page state that outlives the dialog, not logged, and not written to storage.
 * Closing the dialog drops it. The dialog cannot be dismissed by clicking outside
 * or pressing Escape, so the key is not lost by accident; the admin confirms with
 * "I've saved it".
 */
export default function RevealKeyDialog({ result, rolledGraceLabel, onClose }) {
    const contentRef = useRef(null);
    const [test, setTest] = useState(null); // null | 'loading' | {status, ms, org, scopes}
    const open = Boolean(result?.key);

    useEffect(() => {
        setTest(null);
    }, [result?.key]);

    if (!open) return null;

    const rolled = Boolean(rolledGraceLabel);
    const curl = `curl ${publicBaseUrl()}/me \\\n  -H "Authorization: Bearer ${result.key}"`;

    const copy = async () => {
        // The fallback textarea goes inside the dialog: outside it, the focus trap
        // pulls focus back and the copy silently copies nothing.
        const ok = await copyToClipboard(result.key, contentRef.current);
        if (ok) toast.success('Key copied');
        else toast.error('Select the key and copy it manually');
    };

    const runTest = async () => {
        setTest('loading');
        try {
            const res = await testKey(result.key);
            setTest({
                status: res.status,
                ms: res.ms,
                org: res.data?.organization?.name,
                scopes: res.data?.scopes?.length,
                code: res.data?.error?.code,
            });
        } catch {
            setTest({ status: 0, ms: 0, code: 'network_error' });
        }
    };

    return (
        <Dialog open onOpenChange={() => {}}>
            <DialogContent
                className="sm:max-w-lg"
                hideClose
                ref={contentRef}
                data-testid="reveal-key-dialog"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CircleCheck className="w-5 h-5 text-emerald-700" />
                        {rolled ? `New key for ${result.name}` : `${result.name} is ready`}
                    </DialogTitle>
                    <DialogDescription>{scopeSentence(result.scopes)}</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2.5 bg-stone-50 border border-dashed border-violet-600 rounded-lg px-3.5 py-3">
                        <code className="flex-1 font-mono text-[12.5px] break-all select-all" data-testid="raw-key">{result.key}</code>
                        <button type="button" onClick={copy} className="h-8 px-3 rounded-md border border-stone-200 bg-white text-[13px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                            <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                    </div>
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2.5 text-[13px]">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                            Copy it now. You won’t be able to see this key again. If you lose it, roll or revoke it and create a new one.
                            {rolled && rolledGraceLabel !== 'Stop now' && ` The old key keeps working for ${rolledGraceLabel} so you can swap it without losing leads.`}
                        </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-stone-900">Test it</span>
                        <pre className="bg-stone-900 text-stone-200 rounded-lg px-4 py-3 font-mono text-xs whitespace-pre-wrap break-all">{curl}</pre>
                    </div>
                    {test === 'loading' && (
                        <div className="flex items-center gap-2 text-[13px] px-3 py-2.5 rounded-lg bg-stone-50 border border-stone-200 text-stone-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Calling GET /me…
                        </div>
                    )}
                    {test && test !== 'loading' && (
                        test.status === 200 ? (
                            <div className="flex items-center gap-2 text-[13px] px-3 py-2.5 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-700" data-testid="test-result">
                                <CircleCheck className="w-4 h-4" />
                                <span><b>200 OK</b> · {test.org} · {test.scopes} permissions · {test.ms} ms</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-[13px] px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-800" data-testid="test-result">
                                <AlertTriangle className="w-4 h-4" />
                                <span>
                                    <b>{test.status || 'No response'}</b> {test.code ? `· ${test.code}` : ''}
                                    {test.status === 401 && result.ip_allowlist?.length > 0 && (
                                        <span className="block text-xs mt-0.5" data-testid="test-ip-hint">
                                            This key only accepts its allowed IP addresses, and this test comes from your browser. Test it from an allowed server instead.
                                        </span>
                                    )}
                                </span>
                            </div>
                        )
                    )}
                    <p className="text-xs text-stone-500 m-0">
                        The test is a real request: it appears in the request log and updates the key’s “last used”.
                    </p>
                </div>

                <DialogFooter className="gap-2">
                    <button type="button" onClick={runTest} className="h-9 px-4 rounded-md border border-stone-200 text-sm font-medium inline-flex items-center gap-2 hover:bg-stone-50" data-testid="test-key">
                        <Send className="w-3.5 h-3.5" /> Send test request
                    </button>
                    <button type="button" onClick={onClose} className="h-9 px-4 rounded-md bg-stone-900 text-white text-sm font-medium hover:bg-stone-800" data-testid="saved-key">
                        I’ve saved it
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
